import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PROJECT_MANAGEMENT_URL } from '@insite-store360/shared';
import { Observable, catchError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class DeliveryScheduleService {
  private readonly URL_PATH = `${PROJECT_MANAGEMENT_URL}/deliverySchedule`;

  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }

  /**
   * Schedule delivery for a lead
   */
  public scheduleDelivery(
    leadId: number,
    scheduleData: Array<{
      model: string;
      pnc: string;
      quotationQuantity: number;
      deliveryNotificationId?: number;
      deliveries: Array<{
        quantity: number;
        deliveryDate: string;
        deliveryNotificationId?: number;
      }>
    }>,
    batchId: number,
    countryId: number,
    deliveryNotificationId?: number,
    isReschedule?: boolean
  ): Observable<{ message: string }> {
    return this.http
      .post<{ message: string }>(`${this.URL_PATH}/schedule/${leadId}`, {
        scheduleData,
        batchId,
        countryId,
        deliveryNotificationId,
        isReschedule,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Check if delivery schedule exists for a lead
   */
  public hasScheduleForLead(leadId: number): Observable<{ hasSchedule: boolean }> {
    return this.http
      .get<{ hasSchedule: boolean }>(`${this.URL_PATH}/getByLeadId/${leadId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get delivery schedule history for a lead
   */
  public getDeliveryHistory(leadId: number, batchId?: number): Observable<{
    history: Array<{
      deliveryDate: string;
      modelCount: number;
      modelNumbers: string;
      deliveredQty: number;
      status: 'DELIVERED' | 'SCHEDULED';
      address: string;
      recipientName: string;
    }>
  }> {
    let params = new HttpParams();
    if (batchId !== undefined && batchId !== null) {
      params = params.set('batchId', batchId.toString());
    }
    return this.http
      .get<{
        history: Array<{
          deliveryDate: string;
          modelCount: number;
          modelNumbers: string;
          deliveredQty: number;
          status: 'DELIVERED' | 'SCHEDULED';
          address: string;
          recipientName: string;
        }>
      }>(`${this.URL_PATH}/history/${leadId}`, { params })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get leads by country, customer IDs, project names and/or models for setup delivery
   */
  public getLeadsByCustomer(
    countryId: number,
    customerIds: (number | string)[],
    projectNames?: (string)[],
    models?: (string)[]
  ): Observable<{ result: any[] }> {
    let url = `${PROJECT_MANAGEMENT_URL}/setupDelivery/leadsByCustomer`;
    const params: string[] = [];

    // Country and customer IDs are required
    params.push(`countryId=${countryId}`);
    if (customerIds && customerIds.length > 0) {
      params.push(`customerIds=${customerIds.map(id => id.toString()).join(',')}`);
    }

    // Project names (optional)
    if (projectNames && projectNames.length > 0) {
      params.push(`projectNames=${projectNames.map(name => encodeURIComponent(name)).join(',')}`);
    }

    // Models (optional)
    if (models && models.length > 0) {
      params.push(`models=${models.map(model => encodeURIComponent(model)).join(',')}`);
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http
      .get<{ result: any[] }>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get distinct models by customer IDs and optionally project names
   */
  public getModelsByCustomer(
    countryId: number,
    customerIds: (number | string)[],
    projectNames?: (string)[]
  ): Observable<{ id: string; name: string }[]> {
    const params: string[] = [];
    params.push(`countryId=${countryId}`);

    if (customerIds && customerIds.length > 0) {
      params.push(`customerIds=${customerIds.map(id => id.toString()).join(',')}`);
    }

    if (projectNames && projectNames.length > 0) {
      params.push(`projectNames=${projectNames.map(name => encodeURIComponent(name)).join(',')}`);
    }

    const url = `${PROJECT_MANAGEMENT_URL}/setupDelivery/modelsByCustomer?${params.join('&')}`;

    return this.http
      .get<{ id: string; name: string }[]>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get project names by customer IDs
   */
  public getProjectNamesByCustomers(
    countryId: number,
    customerIds: (number | string)[]
  ): Observable<any> {
    const params: string[] = [];
    params.push(`countryId=${countryId}`);

    if (customerIds && customerIds.length > 0) {
      params.push(`customerIds=${customerIds.map(id => id.toString()).join(',')}`);
    }

    const url = `${PROJECT_MANAGEMENT_URL}/setupDelivery/projectNamesByCustomers?${params.join('&')}`;

    return this.http
      .get<any>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get delivery logs by customer with optional filters
   */
  public getDeliveryLogsByCustomer(
    customerId?: number,
    countryIds?: number | string | (number | string)[],
    filters?: {
      leadReferenceNos?: (string | number)[];
      months?: (string | number)[];
      years?: (string | number)[];
      models?: (string | number)[];
      customerIds?: (string | number)[];
      pncs?: (string | number)[];
      probabilityIds?: (string | number)[];
      startDate?: string;
      endDate?: string;
    }
  ): Observable<{ result: any[] }> {
    let url = `${PROJECT_MANAGEMENT_URL}/setupDelivery/deliveryLogsByCustomer`;
    const params: string[] = [];
    if (customerId) {
      params.push(`customerId=${customerId}`);
    }

    if (countryIds) {
      const ids = Array.isArray(countryIds) ? countryIds.join(',') : countryIds.toString();
      if (ids) {
        params.push(`countryId=${ids}`);
      }
    }

    if (filters) {
      if (filters.leadReferenceNos && filters.leadReferenceNos.length > 0) {
        params.push(`leadReferenceNos=${filters.leadReferenceNos.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.months && filters.months.length > 0) {
        params.push(`months=${filters.months.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.years && filters.years.length > 0) {
        params.push(`years=${filters.years.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.models && filters.models.length > 0) {
        params.push(`models=${filters.models.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.customerIds && filters.customerIds.length > 0) {
        params.push(`customerIds=${filters.customerIds.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.pncs && filters.pncs.length > 0) {
        params.push(`pncs=${filters.pncs.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.probabilityIds && filters.probabilityIds.length > 0) {
        params.push(`probabilityIds=${filters.probabilityIds.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.startDate) {
        params.push(`startDate=${encodeURIComponent(filters.startDate)}`);
      }
      if (filters.endDate) {
        params.push(`endDate=${encodeURIComponent(filters.endDate)}`);
      }
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http
      .get<{ result: any[] }>(url)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updateDeliverySchedule(
    deliveryScheduleId: number,
    deliveryDate: string
  ): Observable<{ message: string }> {
    return this.http
      .put<{ message: string }>(`${PROJECT_MANAGEMENT_URL}/setupDelivery/updateDeliveryLog/${deliveryScheduleId}`, {
        deliveryDate,
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Cancel a scheduled delivery
   */
  public cancelDeliverySchedule(
    deliveryScheduleId: number
  ): Observable<{ message: string }> {
    return this.http
      .put<{ message: string }>(`${PROJECT_MANAGEMENT_URL}/setupDelivery/cancelDeliveryLog/${deliveryScheduleId}`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all schedules for a given lead
   */
  public getSchedulesByLeadId(leadId: number): Observable<any[]> {
    return this.http
      .get<any[]>(`${this.URL_PATH}/getSchedules/${leadId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Export delivery logs to Excel
   */
  public exportDeliveryLogs(
    customerId?: number,
    countryIds?: number | string | (number | string)[],
    filters?: {
      leadReferenceNos?: (string | number)[];
      months?: (string | number)[];
      years?: (string | number)[];
      models?: (string | number)[];
      customerIds?: (string | number)[];
      pncs?: (string | number)[];
      probabilityIds?: (string | number)[];
      startDate?: string;
      endDate?: string;
    }
  ): Observable<Blob> {
    let url = `${PROJECT_MANAGEMENT_URL}/setupDelivery/downloadExcelReport`;
    const params: string[] = [];
    if (customerId) {
      params.push(`customerId=${customerId}`);
    }

    if (countryIds) {
      const ids = Array.isArray(countryIds) ? countryIds.join(',') : countryIds.toString();
      if (ids) {
        params.push(`countryId=${ids}`);
      }
    }

    if (filters) {
      if (filters.leadReferenceNos && filters.leadReferenceNos.length > 0) {
        params.push(`leadReferenceNos=${filters.leadReferenceNos.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.months && filters.months.length > 0) {
        params.push(`months=${filters.months.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.years && filters.years.length > 0) {
        params.push(`years=${filters.years.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.models && filters.models.length > 0) {
        params.push(`models=${filters.models.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.customerIds && filters.customerIds.length > 0) {
        params.push(`customerIds=${filters.customerIds.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.pncs && filters.pncs.length > 0) {
        params.push(`pncs=${filters.pncs.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.probabilityIds && filters.probabilityIds.length > 0) {
        params.push(`probabilityIds=${filters.probabilityIds.map(v => encodeURIComponent(v.toString())).join(',')}`);
      }
      if (filters.startDate) {
        params.push(`startDate=${encodeURIComponent(filters.startDate)}`);
      }
      if (filters.endDate) {
        params.push(`endDate=${encodeURIComponent(filters.endDate)}`);
      }
    }

    if (params.length > 0) {
      url += `?${params.join('&')}`;
    }

    return this.http.get(url, { responseType: 'blob' });
  }
}

